// app/src/features/boot/useBootWiring.js
import { useCallback, useEffect, useRef } from "react";
import { useBootForecasts } from "../../hooks/useBootForecasts.js";
import { useLinkClaim } from "../../hooks/useLinkClaim.js";
import { useSession } from "../../hooks/useSession.js";
import { useSharedForecasts } from "../../hooks/useSharedForecasts.js";
import {
  selectBranchRootForecast,
  selectFocusableForecastById,
  selectLinkedBranchRun,
} from "../../lib/forecast/selectors.js";
import { readViewFromLocation } from "../../lib/view/urlSync.js";

/**
 * The boot wiring: the owner lane's load / restore / resume (`useBootForecasts`, its 15-field
 * argument object passed unchanged — S-11), the shared-with-me lane (`useSharedForecasts`), the
 * link-share claim (`useLinkClaim`, gated on the conservative miss test) and the one-shot view
 * init. Called where `AppInner` called `useBootForecasts`: after the timeline geometry (whose
 * refs the view init writes) and before the realtime channel, which reads `forecastsLoaded`.
 *
 * `useSharedAccessNotice` is NOT here: its `noticeShown` is read by the tracking prefs and the
 * revoke-my-access hook, so the call stays in `AppInner` and the strip (`AccessNoticeStrip`)
 * reads `accessNotice` from the frame context. The run store reaches this hook as `runStore`
 * (injected, never imported — C-23). `me`, `authEnabled`, `authLoading` and
 * `forecastLiveProgressEnabled` are read from the session context through `useSession()`.
 *
 * @param {object} deps
 * @param {object} deps.runStore  the run store (injected, never imported — C-23)
 * @param {Function} deps.setPinned  the pinned-map setter (AppInner's `useState`)
 * @param {{current: boolean}} deps.selectForecastRef  forward ref AppInner assigns after this call
 * @param {{current: Function|null}} deps.resumeTourRef  forward ref AppInner assigns after this call
 */
export function useBootWiring({
  // useBootForecasts' argument object (S-11, 15 fields; four session names come from the context)
  forecasts,
  pinned,
  setPinned,
  autoZoomRef,
  setForecastingPhase,
  setPendingPreprocess,
  setLastObs,
  setCompareMode,
  debugUI,
  selectForecastRef,
  resumeTourRef,
  // the link claim's miss test
  runStore,
  // the view init's
  measured,
  cSize,
  todayDay,
  scheduleRender,
  vlRef,
  ppdRef,
}) {
  const { me, authEnabled, authLoading, forecastLiveProgressEnabled } = useSession();
  // Load, restore decision and resume sweep are composed as ONE ordered command
  // (commands/bootForecasts.ts) invoked from ONE mount effect inside this hook.
  const {
    bootPhase,
    forecastsLoaded,
    pendingRestore,
    loadConversationThread,
    reattachToForecast,
    loadedRef,
    claimLoadSlot,
    isCurrentLoadSlot,
  } = useBootForecasts({
    me,
    authEnabled,
    authLoading,
    forecasts,
    pinned,
    setPinned,
    autoZoomRef,
    setForecastingPhase,
    setPendingPreprocess,
    setLastObs,
    forecastLiveProgressEnabled,
    setCompareMode,
    debugUI,
    selectForecastRef,
    resumeTourRef,
  });

  // Shared-with-me lane: owns its own load lifecycle (useSharedForecasts's own useState/useRef,
  // never lifted here, because the ratchet counts AppInner useState). The rows come from the store
  // via selectSharedForecasts, same as `forecasts` above does for the owner leg; `runState` already
  // re-renders this component on every store change, so no separate subscription is needed.
  const {
    loading: sharedLoading,
    hasMore: sharedHasMore,
    error: sharedError,
    loadMore: onLoadMoreShared,
    reload: reloadShared,
    settled: sharedSettled,
  } = useSharedForecasts({
    // The shared lane's rows carry the VIEWER's own watchlist membership, and both
    // lanes feed this one map. `{...page, ...prev}` (not the reverse) so an
    // optimistic pin made since the fetch started still wins — the order
    // bootForecasts uses for the owner leg, for the same reason.
    onPinned: useCallback(
      (pagePinned) =>
        setPinned((prev) =>
          // RETURN `prev` ITSELF when the page pinned nothing — React bails out of a re-render on
          // an identical object, where a fresh spread would mint a new `pinned` identity on every
          // shared-lane load.
          Object.keys(pagePinned || {}).length ? { ...pagePinned, ...prev } : prev,
        ),
      // setPinned is a useState setter (stable), listed so the compiler's inferred deps match.
      [setPinned],
    ),
  });

  // ── RECEIVING A LINK-SHARED FORECAST ──────────────────────────────────────
  // A link share does not enumerate (see listSharedForecasts' filter), so a
  // recipient arriving on `?forecast=<uuid>` holds an id NEITHER lane contains.
  // The claim endpoint is the receive event: it records a grant naming them, and
  // the reload then brings the row in — at the top of Recent, because the claim's
  // timestamp is the received-at the list sorts by.
  //
  // The MISS TEST is deliberately conservative: both lanes settled AND the store
  // genuinely does not have it, even after paging the shared lane in (below). Claiming for a
  // forecast already in hand is a pointless write; claiming before the lanes settle would fire
  // for every viewer on every boot. `useLinkClaim` additionally never retries a refusal.
  //
  // PAGING FIRST (SOO-862): the shared lane loads a page at a time, so the linked forecast, or a
  // linked branch's root or intermediate parent, can be on a page not loaded yet. While the link
  // does not resolve to a root in hand and the lane has more pages, load the next one; the deep
  // link waits on "paging". The last page, or a failed one (`sharedError`), ends the paging.
  const deepLinkWantedId = readViewFromLocation().activeId;
  const deepLinkState = runStore.getState();
  const lanesSettled = Boolean(deepLinkWantedId) && forecastsLoaded && sharedSettled;
  const deepLinkRoot = lanesSettled
    ? selectFocusableForecastById(deepLinkState, deepLinkWantedId)
    : null;
  const deepLinkBranch =
    lanesSettled && !deepLinkRoot ? selectLinkedBranchRun(deepLinkState, deepLinkWantedId) : null;
  const deepLinkUnresolved =
    lanesSettled &&
    !deepLinkRoot &&
    (!deepLinkBranch || !selectBranchRootForecast(deepLinkState, deepLinkBranch));
  const linkPaging = deepLinkUnresolved && sharedHasMore && !sharedError;
  useEffect(() => {
    if (!linkPaging || sharedLoading) return;
    // loadMore never throws: a failed page sets `sharedError`, which ends the paging above.
    void onLoadMoreShared();
  }, [linkPaging, sharedLoading, onLoadMoreShared]);
  // A branch in hand is not a miss: the deep link opens its root, or says it cannot.
  const deepLinkMissing = deepLinkUnresolved && !deepLinkBranch && !linkPaging;
  const { claimed: linkClaimSucceeded, refused: linkClaimRefused } = useLinkClaim({
    forecastId: deepLinkWantedId,
    enabled: deepLinkMissing,
    onClaimed: reloadShared,
  });

  // One primitive for the deep link's effect deps: "paging" while the shared lane is paged in for
  // the link, else "pending" until the claim settles. "paging" is its own value so the deep link
  // re-runs on the edge where paging ends.
  const linkClaim = linkPaging
    ? "paging"
    : linkClaimSucceeded
      ? "claimed"
      : linkClaimRefused
        ? "refused"
        : "pending";

  const viewInitRef = useRef(false);
  // Fires exactly once, but waits until auth has resolved AND the container has been measured (the
  // explicit `measured` flag, not cSize.w, which is initialized to a placeholder of 800 and so is
  // always truthy from mount). The viewInitRef guard prevents re-firing on later cSize changes,
  // which would wipe out the user's pan position on browser resize / panel toggles.
  useEffect(() => {
    if (viewInitRef.current) return;
    if (authLoading || !measured) return;
    viewInitRef.current = true;
    vlRef.current = todayDay - (cSize.w * 0.47) / ppdRef.current;
    scheduleRender();
  }, [authLoading, measured, cSize.w, todayDay, scheduleRender]);

  return {
    bootPhase,
    forecastsLoaded,
    pendingRestore,
    loadConversationThread,
    reattachToForecast,
    loadedRef,
    claimLoadSlot,
    isCurrentLoadSlot,
    sharedLoading,
    sharedHasMore,
    sharedError,
    onLoadMoreShared,
    sharedSettled,
    linkClaim,
  };
}
